import React, { useEffect, useRef } from 'react';
import { YearbookPage, PAGE_WIDTH, PAGE_HEIGHT } from '@/types/yearbook';
import ReadOnlyElement from '../ReadOnlyElement';

interface MobilePageStripProps {
  pages: YearbookPage[];
  currentPageIndex: number;
  onSelectPage: (index: number) => void;
  loadedIndices: Set<number>;
  coverName?: string;
}

const THUMB_HEIGHT = 56;

const MobilePageStrip: React.FC<MobilePageStripProps> = ({
  pages,
  currentPageIndex,
  onSelectPage,
  loadedIndices,
  coverName,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbW = (THUMB_HEIGHT * PAGE_WIDTH) / PAGE_HEIGHT;
  const scale = THUMB_HEIGHT / PAGE_HEIGHT;

  // Auto-scroll to active thumb
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const active = container.querySelector<HTMLElement>(`[data-thumb-idx="${currentPageIndex}"]`);
    if (!active) return;
    const cRect = container.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    const offset = aRect.left - cRect.left - cRect.width / 2 + aRect.width / 2;
    container.scrollBy({ left: offset, behavior: 'smooth' });
  }, [currentPageIndex]);

  return (
    <div
      ref={containerRef}
      className="h-[72px] shrink-0 bg-editor-topbar border-t border-editor-sidebar-border flex items-center gap-2 px-2 overflow-x-auto"
      style={{ scrollbarWidth: 'none' }}
    >
      {pages.map((page, idx) => {
        const isActive = idx === currentPageIndex;
        const isLoaded = loadedIndices.has(idx);
        const label = page.coverType === 'front'
          ? (coverName ? coverName : '1ère')
          : page.coverType === 'back'
          ? '4ème'
          : `${page.pageNumber}`;
        return (
          <button
            key={page.id}
            data-thumb-idx={idx}
            onClick={() => onSelectPage(idx)}
            className={`shrink-0 flex flex-col items-center gap-0.5 transition-all ${
              isActive ? 'scale-105' : 'opacity-60'
            }`}
          >
            <div
              className={`relative bg-white overflow-hidden rounded-sm ${
                isActive
                  ? 'ring-2 ring-editor-sidebar-active'
                  : 'ring-1 ring-editor-sidebar-border'
              }`}
              style={{ width: thumbW, height: THUMB_HEIGHT }}
            >
              {isLoaded ? (
                <>
                  {page.background.type === 'image' && (
                    <img
                      src={page.background.value}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      draggable={false}
                    />
                  )}
                  <div
                    className="origin-top-left absolute inset-0"
                    style={{
                      width: PAGE_WIDTH,
                      height: PAGE_HEIGHT,
                      transform: `scale(${scale})`,
                      backgroundColor:
                        page.background.type === 'color' ? page.background.value : undefined,
                    }}
                  >
                    {page.elements.map((el) => (
                      <ReadOnlyElement key={el.id} element={el} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="w-full h-full bg-muted" />
              )}
            </div>
            <span
              className={`text-[9px] leading-none ${
                isActive
                  ? 'text-editor-sidebar-active font-semibold'
                  : 'text-editor-sidebar-foreground/60'
              }`}
            >
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default MobilePageStrip;
